/* eslint-disable react/display-name */
import { Button, Popconfirm, Space, Tooltip } from "antd";
import React from "react";
import { Link, useHistory } from "react-router-dom";
import moment from "moment";
import TableBase from "../../../../../components/table/TableBase";
import RouteStatus from "../item/RouteStatus";
import "../../styles/routes.scss";
import { RouteIcon, ExportIcon } from "components/svgs";
import FacturaCell from "../../../../../components/FacturaCell/FacturaCell";
import { doExportCsv } from "helper/export";
import {
  ArrowRightOutlined,
  RetweetOutlined,
  CloseCircleOutlined,
  UserDeleteOutlined,
  FilePdfOutlined
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { selectRole } from "modules/auth/store/authSlice";
import { allowPermission } from "helper/util";
import { canEditPermission, INVOICE_TYPES } from "configs/constants";
import { convertEmergencyStatus, getTimeZone } from "helper/util";
import EditPriceInput from "./EditPriceInput";

const RouteTable = ({
  dataSource,
  pagination,
  hiddenColumns = [],
  onCancelRoute,
  onAssignDriverClick,
  onReassignDriverClick,
  onUnassignDriverClick,
  selectedRoutes = [],
  onRouteSelection,
  enableMultiSelect = false,
  onRefresh,
  onSortChange,
  currentSort,
  onExportInvoicePdf
}) => {
  const canEdit = allowPermission(useSelector(selectRole), canEditPermission);
  const history = useHistory();

  // Format datetime for display - combines date and time if both are available
  const formatDateTime = (datetime, pickupTime) => {
    if (!datetime) return "N/A";

    // If we have both date and time, combine them
    if (pickupTime && pickupTime !== "N/A" && pickupTime !== "null") {
      // Parse the date part and time part separately
      const datePart = moment(datetime).format("YYYY-MM-DD");
      // Combine date and time (pickupTime is already formatted as HH:mm)
      const combined = moment(`${datePart} ${pickupTime}`, "YYYY-MM-DD HH:mm");

      return (
        <div style={{ whiteSpace: "pre-line", lineHeight: "1.3" }}>
          {combined.format("YYYY-")}
          <br />
          {combined.format("MM-DD")}
          <br />
          {combined.format("HH:mm")}
        </div>
      );
    }

    // Otherwise just use the datetime as-is
    const m = moment(datetime);
    return (
      <div style={{ whiteSpace: "pre-line", lineHeight: "1.3" }}>
        {m.format("YYYY-")}
        <br />
        {m.format("MM-DD")}
        <br />
        {m.format("HH:mm")}
      </div>
    );
  };

  const onRowOrder = (record) => {
    return {
      onClick: () => {
        history.push(`/admin/routes/${record.id}`);
      } // click row
    };
  };

  const handleExport = async (record) => {
    await doExportCsv({
      path: `routes/export/${record.id}?tz=${getTimeZone()}`,
      name: record.id
    });
  };

  const canEditPrice = (record) => {
    // Only check factura status - disable editing if factura is paid or cancelled
    if (record?.factura?.status === "paid" || record?.factura?.status === "cancelled") {
      return false;
    }

    // Allow editing for all other cases (no factura, or unpaid/partial/overdue factura)
    return true;
  };

  // Handle row selection for multi-select
  const handleRowSelectionChange = (selectedRowKeys) => {
    if (onRouteSelection) {
      onRouteSelection(selectedRowKeys);
    }
  };

  // Row selection configuration
  const rowSelection = enableMultiSelect
    ? {
        selectedRowKeys: selectedRoutes,
        onChange: handleRowSelectionChange,
        getCheckboxProps: (record) => ({
          disabled: !!record.factura // Disable selection if route already has a factura
        }),
        hideSelectAll: false,
        showSizeChanger: false
      }
    : null;

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      fixed: "left",
      width: 120,
      render: (value) => {
        return (
          <p style={{ margin: 0, color: "#1f3998" }}>
            {value.substr(0, 8).toUpperCase()}
          </p>
        );
      }
    },
    {
      title: "Client",
      dataIndex: "client",
      width: 100,
      render: (value) => {
        return value?.clientName;
      }
    },
    {
      title: "Driver",
      dataIndex: "driver",
      width: 100,
      render: (value) => {
        return value ? value.firstName + " " + value.lastName : "N/A";
      }
    },
    {
      title: "# of stops",
      dataIndex: "routeLocations",
      width: 100,
      render: (value) => {
        return value?.length || "N/A";
      }
    },
    {
      title: (
        <Tooltip title="Pickup Date">
          <span>PU</span>
        </Tooltip>
      ),
      width: 90,
      dataIndex: "startDate",
      sorter: true,
      sortOrder:
        currentSort?.field === "startDate"
          ? currentSort.order === "ASC"
            ? "ascend"
            : "descend"
          : false,
      render: (value, record) => formatDateTime(value, record.pickupTime)
    },
    {
      title: (
        <Tooltip title="Created At">
          <span>CA</span>
        </Tooltip>
      ),
      width: 90,
      dataIndex: "createdAt",
      sorter: true,
      sortOrder:
        currentSort?.field === "createdAt"
          ? currentSort.order === "ASC"
            ? "ascend"
            : "descend"
          : false,
      render: (value) => formatDateTime(value)
    },
    {
      title: (
        <Tooltip title="Last Updated">
          <span>LU</span>
        </Tooltip>
      ),
      width: 90,
      dataIndex: "updatedAt",
      sorter: true,
      sortOrder:
        currentSort?.field === "updatedAt"
          ? currentSort.order === "ASC"
            ? "ascend"
            : "descend"
          : false,
      render: (value) => formatDateTime(value)
    },
    {
      title: "Pickup Time",
      dataIndex: "pickupTime",
      width: 130,
      render: (value) => {
        return value === "null" ? "N/A" : value;
      }
    },
    {
      title: "Min. Vehicle",
      dataIndex: "vehicleCategory",
      width: 130,
      render: (value) => {
        return value?.name || "N/A";
      }
    },
    {
      title: "Status",
      // dataIndex: "status",
      width: 110,
      render: (record) => <RouteStatus value={convertEmergencyStatus(record)} />
    },
    {
      title: "Factura",
      width: 120,
      render: (record) => <FacturaCell factura={record.factura} isAdmin={true} />
    },
    {
      title: "Client Estimate",
      dataIndex: "clientEstimatePrice",
      width: 130,
      render: (value, record) => {
        return (
          <EditPriceInput
            value={value}
            record={record}
            priceType="clientEstimatePrice"
            typeName="typeClient"
            showIcon={false}
            onRefresh={onRefresh}
          />
        );
      }
    },
    {
      title: "Driver Estimate",
      dataIndex: "driverEstimatePrice",
      width: 130,
      render: (value, record) => {
        return (
          <EditPriceInput
            value={value}
            record={record}
            priceType="driverEstimatePrice"
            typeName="typeDriver"
            showIcon={false}
            onRefresh={onRefresh}
          />
        );
      }
    },
    {
      title: "Client Final",
      dataIndex: "clientFinalPrice",
      width: 130,
      render: (value, record) => {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
            <EditPriceInput
              value={value}
              record={record}
              priceType="clientFinalPrice"
              typeName="typeClient"
              showIcon={false}
              disabled={!canEditPrice(record)}
              onRefresh={onRefresh}
            />
            {onExportInvoicePdf && (
              <Tooltip title="Export Client Invoice PDF">
                <Button
                  type="link"
                  size="small"
                  icon={<FilePdfOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onExportInvoicePdf(record.id, INVOICE_TYPES.CLIENT_INVOICE);
                  }}
                  style={{ padding: "0 2px", minWidth: "auto" }}
                />
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      title: "Driver Final",
      dataIndex: "driverFinalPrice",
      width: 130,
      render: (value, record) => {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
            <EditPriceInput
              value={value}
              record={record}
              priceType="driverFinalPrice"
              typeName="typeDriver"
              showIcon={false}
              disabled={!canEditPrice(record)}
              onRefresh={onRefresh}
            />
            {onExportInvoicePdf && (
              <Tooltip title="Export Driver Invoice PDF">
                <Button
                  type="link"
                  size="small"
                  icon={<FilePdfOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onExportInvoicePdf(record.id, INVOICE_TYPES.DRIVER_INVOICE);
                  }}
                  style={{ padding: "0 2px", minWidth: "auto" }}
                />
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      title: "",
      width: 40,
      render: (record) => {
        // Check if any price has been manually edited
        // For estimates: more than 1 entry means edited (first one is automatic)
        // For finals: 1 or more entries means edited (no automatic entry)
        const clientEstimates =
          record?.routeEstimates?.filter((re) => re?.type === "client_estimate") || [];
        const driverEstimates =
          record?.routeEstimates?.filter((re) => re?.type === "driver_estimate") || [];
        const clientFinals =
          record?.finalPrices?.filter((fp) => fp?.type === "client_final_price") || [];
        const driverFinals =
          record?.finalPrices?.filter((fp) => fp?.type === "driver_final_price") || [];

        const hasClientEstimateHistory = clientEstimates.length > 1;
        const hasDriverEstimateHistory = driverEstimates.length > 1;
        const hasClientFinalHistory = clientFinals.length >= 1;
        const hasDriverFinalHistory = driverFinals.length >= 1;

        const hasAnyPriceEdit =
          hasClientEstimateHistory ||
          hasDriverEstimateHistory ||
          hasClientFinalHistory ||
          hasDriverFinalHistory;

        // Debug logging for the specific route
        if (record?.id?.startsWith("cb6ee049")) {
          console.log("=== Frontend RouteTable Debug ===");
          console.log("Route ID:", record.id);
          console.log("routeEstimates:", record.routeEstimates);
          console.log("clientEstimates length:", clientEstimates.length);
          console.log("driverEstimates length:", driverEstimates.length);
          console.log("hasClientEstimateHistory:", hasClientEstimateHistory);
          console.log("hasDriverEstimateHistory:", hasDriverEstimateHistory);
          console.log("hasAnyPriceEdit:", hasAnyPriceEdit);
          console.log("==================================");
        }

        return hasAnyPriceEdit ? (
          <EditPriceInput
            record={record}
            priceType="priceHistory"
            typeName="priceHistory"
            iconOnly={true}
            manualUpdate={true}
            showIcon={true}
          />
        ) : null;
      }
    },
    {
      title: "",
      key: "action",
      width: 150,
      fixed: "right",
      render: (_, record) => {
        if (
          record.pickupTime === "null" &&
          record.jobs?.length === 1 &&
          record.jobs[0].pickupAddress === record.jobs[0].dropoffAddress
        ) {
          return <></>;
        } else {
          return (
            <Space>
              <Tooltip title="Export Job">
                <div onClick={(e) => e.stopPropagation()}>
                  <ExportIcon onClick={() => handleExport(record)} />
                </div>
              </Tooltip>
              {!["canceled", "completed"].includes(record.status) && canEdit && (
                <Tooltip title="Cancel Job">
                  <div onClick={(e) => e.stopPropagation()}>
                    <Popconfirm
                      title="Are you sure to cancel this job?"
                      onConfirm={() => onCancelRoute && onCancelRoute(record.id)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <CloseCircleOutlined />
                    </Popconfirm>
                  </div>
                </Tooltip>
              )}
              {record.status === "pending" && canEdit && (
                <Tooltip title="Assign Driver">
                  <Button
                    type="text"
                    className="no-padding"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAssignDriverClick && onAssignDriverClick(record);
                    }}
                  >
                    <ArrowRightOutlined />
                  </Button>
                </Tooltip>
              )}
              {["accepted", "in-progress"].includes(record.status) && canEdit && (
                <Tooltip title="Reassign Driver">
                  <Button
                    type="text"
                    className="no-padding"
                    onClick={(e) => {
                      e.stopPropagation();
                      onReassignDriverClick && onReassignDriverClick(record);
                    }}
                  >
                    <RetweetOutlined />
                  </Button>
                </Tooltip>
              )}
              {/* Unassign driver button, renders only if the route status is "Accepted" and has a driver assigned */}
              {record.status === "accepted" && canEdit && record.driverId !== null && (
                <Tooltip placement="bottomLeft" title="Unassign Driver">
                  <div className="no-padding" onClick={(e) => e.stopPropagation()}>
                    <Popconfirm
                      placement="topLeft"
                      title="Are you sure to unassign this driver?"
                      onConfirm={() =>
                        onUnassignDriverClick && onUnassignDriverClick(record.id)
                      }
                      okText="Yes"
                      cancelText="No"
                    >
                      <UserDeleteOutlined />
                    </Popconfirm>
                  </div>
                </Tooltip>
              )}
            </Space>
          );
        }
      }
    }
  ];
  const handleTableChange = (paginationConfig, filters, sorter) => {
    console.log("RouteTable - handleTableChange:", sorter);
    if (onSortChange) {
      if (sorter && sorter.field && sorter.order) {
        // User clicked to sort - apply the sort
        const order = sorter.order === "ascend" ? "ASC" : "DESC";
        onSortChange(sorter.field, order);
      } else if (sorter && sorter.field && !sorter.order) {
        // User clicked to clear sort (order is undefined) - toggle to opposite
        const newOrder = currentSort?.order === "ASC" ? "DESC" : "ASC";
        onSortChange(sorter.field, newOrder);
      }
    }
  };

  const filteredColumns =
    hiddenColumns.length > 0
      ? columns.filter((col) => !hiddenColumns.includes(col.title))
      : columns;
  return (
    <TableBase
      onRow={onRowOrder}
      columns={filteredColumns}
      data={dataSource}
      pagination={pagination}
      rowKey="id"
      rowSelection={rowSelection}
      onChange={handleTableChange}
    />
  );
};
export default RouteTable;
